import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { isAmountLimitCode } from "@/shared/utils/amountLimits";

import { PSE_DETAIL_EXCEEDED_LIMIT } from "./usePseRedirect";

/**
 * Holding screen between creating the payin and landing on the bank's page.
 *
 * The handoff is automatic (`usePseRedirect`), so this screen has no "go to your bank" button — offering one
 * would suggest the payer has something to do while the runtime is still registering the payment. Cancel is
 * the only action, and it exists because the wait is not always short.
 */
export function PseHandoffScreen({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslations("payins");
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <Loader2 className="size-10 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
      <div>
        <p className="text-base font-semibold text-foreground">{t("pse_handoff_title")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("pse_handoff_body")}</p>
      </div>
      <ColocaButton type="button" variant="ghost" onClick={onCancel}>
        {t("pse_handoff_cancel")}
      </ColocaButton>
    </div>
  );
}

/**
 * The debit was authorized and the payin settled while this screen was still open.
 *
 * It exists because the handoff is not the only way a PSE flow can end. The holding screen waits for a bank
 * page, and `usePseRedirect` stops producing one the moment the payin turns terminal — so a payin that
 * reaches CONFIRMED before the redirect fires (the webhook won the race, or the runtime never backfilled a
 * URL) leaves nothing left to wait for. Without this branch that screen spins forever on a collection that
 * was PAID, and the only way out is a Cancel that reads like the charge failed.
 */
export function PseConfirmedScreen({ onNew }: { onNew: () => void }) {
  const { t } = useTranslations("payins");
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-success-container">
        <CheckCircle2 className="size-7 text-success" aria-hidden="true" />
      </div>
      <div>
        <p className="font-semibold text-foreground">{t("pse_confirmed_title")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("pse_confirmed_body")}</p>
      </div>
      <ColocaButton type="button" variant="primary" onClick={onNew}>
        {t("pse_confirmed_new")}
      </ColocaButton>
    </div>
  );
}

export interface PseFailureScreenProps {
  /** Network rejection detail, when the runtime returned one. Selects the mandated copy. */
  detail: string | null;
  /**
   * The backend's own sentence — shown when `detail` is one of our amount-limit codes, since it carries the
   * bound.
   */
  message?: string | null;
  onRetry: () => void;
}

/**
 * The payment never reached the bank — creation failed, or the network rejected the registration.
 *
 * The limit case has copy the network mandates verbatim; an amount outside our own configured range shows the
 * backend's sentence (it names the bound); everything else gets the generic message. All must be shown next
 * to a way to reach a human, which is why the contact lines are part of the screen rather than a footnote
 * somewhere else.
 */
export function PseFailureScreen({ detail, message, onRetry }: PseFailureScreenProps) {
  const { t } = useTranslations("payins");
  const body =
    detail === PSE_DETAIL_EXCEEDED_LIMIT
      ? t("pse_failure_limit")
      : isAmountLimitCode(detail) && message
        ? message
        : t("pse_failure_generic");
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
        <XCircle className="size-7 text-destructive" aria-hidden="true" />
      </div>
      <div>
        <p className="font-semibold text-destructive">{t("pse_failure_title")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      </div>
      <ColocaSurface variant="muted" padding="sm" className="text-sm">
        <p>
          <span className="text-muted-foreground">{t("pse_contact_phone_label")}: </span>
          <span className="font-medium text-foreground">{t("pse_contact_phone")}</span>
        </p>
        <p className="mt-0.5">
          <span className="text-muted-foreground">{t("pse_contact_email_label")}: </span>
          <span className="font-medium text-foreground">{t("pse_contact_email")}</span>
        </p>
      </ColocaSurface>
      <ColocaButton type="button" variant="primary" onClick={onRetry}>
        {t("pse_failure_retry")}
      </ColocaButton>
    </div>
  );
}
